/**
 * How many times the medication photo-upload wizard has been shown on a given
 * visit.
 *
 * Per visit, not per nurse: a nurse who learned the flow on Monday's SOC can
 * still be on a visit where she has not yet photographed anything, and the
 * coaching is cheap. Capped so it never becomes nagging — see
 * `WIZARD_MAX_SHOWS`.
 *
 * Storage is the same web/native split `lib/api-env.ts` and `lib/auth.tsx`
 * already use. AsyncStorage is deliberately NOT a dependency of this app (see
 * the note in `lib/diagnosis-map.ts`), so this rides on expo-secure-store,
 * which is already installed. Every read and write is wrapped: this is a
 * coaching nicety, and a storage failure must never block the nurse from
 * adding a medication.
 */

import * as SecureStore from "expo-secure-store";
import { Platform } from "react-native";

export { WIZARD_MAX_SHOWS, shouldShowWizard } from "./medication-wizard-rules";

// SecureStore keys accept alphanumerics plus ".", "-" and "_" only.
const key = (visitId: string) => `med-wizard-shown-${visitId}`;

async function readRaw(k: string): Promise<string | null> {
    if (Platform.OS === "web") {
        try {
            return localStorage.getItem(k);
        } catch {
            return null;
        }
    }
    return SecureStore.getItemAsync(k);
}

async function writeRaw(k: string, value: string): Promise<void> {
    if (Platform.OS === "web") {
        try {
            localStorage.setItem(k, value);
        } catch {
            /* private-mode browsers throw on write; coaching is not worth a crash */
        }
        return;
    }
    await SecureStore.setItemAsync(k, value);
}

export async function readWizardShownCount(visitId: string): Promise<number> {
    try {
        const raw = await readRaw(key(visitId));
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : 0;
    } catch {
        // Unreadable store: treat as "never shown". Worst case she sees the
        // coaching once more than she should, which is the safe direction.
        return 0;
    }
}

export async function bumpWizardShownCount(visitId: string): Promise<void> {
    try {
        const next = (await readWizardShownCount(visitId)) + 1;
        await writeRaw(key(visitId), String(next));
    } catch {
        /* see above — never block on this */
    }
}
